import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, invalidResponseIssues } from '@/api/errors';

export interface ResolvedError {
  message: string;
  recovery?: string;
  code?: string;
  status?: number;
  /** Diagnostic lines worth showing verbatim, e.g. the schema paths that failed. */
  details?: string[];
}

/**
 * Map an unknown thrown value to a localized message + optional recovery hint.
 * Recovery hints are inline copy ("This appeal was taken by you; pass it to
 * another moderator") that help the operator fix the problem on this screen.
 */
export function useApiError(): (err: unknown) => ResolvedError {
  const { t } = useTranslation('errors');
  return React.useCallback(
    (err: unknown): ResolvedError => {
      if (err instanceof ApiError) {
        const baseKey = err.code;
        const message = t(baseKey, { defaultValue: err.message || t('generic') });
        const recoveryKey = `${baseKey}__hint`;
        const recovery = t(recoveryKey, { defaultValue: '' });
        const resolved: ResolvedError = { message, code: err.code, status: err.status };
        if (recovery) resolved.recovery = recovery;
        const details = invalidResponseIssues(err);
        if (details.length > 0) resolved.details = details;
        return resolved;
      }
      if (err instanceof Error) {
        return { message: err.message || t('generic') };
      }
      return { message: t('generic') };
    },
    [t]
  );
}
